import { useMemo, useState } from 'react';
import { Button } from '../../components/primitives';
import { ListPage } from '../../components/list';
import { StavbaGate } from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useHarmonogramy,
  useHarmonogram,
  useCreateHarmonogram,
  useCreateHarmonogramTask,
} from '../../api/hooks';
import type { HarmonogramTask } from '../../api/schemas';
import styles from './HarmonogramPage.module.css';
import { cx } from '../../lib/cx';

const MONTH_NAMES = [
  'LEDEN', 'ÚNOR', 'BŘEZEN', 'DUBEN', 'KVĚTEN', 'ČERVEN',
  'ČERVENEC', 'SRPEN', 'ZÁŘÍ', 'ŘÍJEN', 'LISTOPAD', 'PROSINEC',
];

interface MonthCell {
  label: string;
  year: number;
  monthIndex: number;
}

function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function buildMonthRange(tasks: HarmonogramTask[], start: Date | null, end: Date | null): MonthCell[] {
  let minMs = start?.getTime() ?? Infinity;
  let maxMs = end?.getTime() ?? -Infinity;
  for (const t of tasks) {
    const od = parseDate(t.datum_od);
    const doD = parseDate(t.datum_do ?? t.datum_od);
    if (od) minMs = Math.min(minMs, od.getTime());
    if (doD) maxMs = Math.max(maxMs, doD.getTime());
  }
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs < minMs) {
    const today = new Date();
    const months: MonthCell[] = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({
        label: MONTH_NAMES[d.getMonth()],
        year: d.getFullYear(),
        monthIndex: d.getMonth(),
      });
    }
    return months;
  }
  const startMonth = new Date(minMs);
  startMonth.setDate(1);
  const endMonth = new Date(maxMs);
  endMonth.setDate(1);
  const out: MonthCell[] = [];
  const cursor = new Date(startMonth);
  while (cursor <= endMonth && out.length < 18) {
    out.push({
      label: MONTH_NAMES[cursor.getMonth()],
      year: cursor.getFullYear(),
      monthIndex: cursor.getMonth(),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  if (out.length === 0) {
    out.push({
      label: MONTH_NAMES[startMonth.getMonth()],
      year: startMonth.getFullYear(),
      monthIndex: startMonth.getMonth(),
    });
  }
  return out;
}

function ratio(date: Date, first: MonthCell, months: MonthCell[]): number {
  const firstMs = new Date(first.year, first.monthIndex, 1).getTime();
  const lastMs = new Date(
    months[months.length - 1].year,
    months[months.length - 1].monthIndex + 1,
    1,
  ).getTime();
  const span = lastMs - firstMs;
  if (span <= 0) return 0;
  const pos = (date.getTime() - firstMs) / span;
  return Math.min(1, Math.max(0, pos));
}

function progressVariant(t: HarmonogramTask): 'done' | 'running' | 'planned' {
  if (t.progress >= 100) return 'done';
  if (t.progress > 0) return 'running';
  return 'planned';
}

export function HarmonogramPage() {
  const { activeStavbaId, activeStavba } = useActiveStavba();
  const { data: list, isLoading: listLoading } = useHarmonogramy(activeStavbaId);
  const firstId = list?.[0]?.id ?? null;
  const { data: h, isLoading: detailLoading } = useHarmonogram(activeStavbaId, firstId);
  const createHarmonogram = useCreateHarmonogram(activeStavbaId);
  const createTask = useCreateHarmonogramTask(activeStavbaId, firstId);
  const [formOpen, setFormOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const months = useMemo(() => {
    const tasks = h?.tasks ?? [];
    return buildMonthRange(tasks, parseDate(h?.datum_zahajeni ?? null), parseDate(h?.datum_ukonceni ?? null));
  }, [h]);

  if (!activeStavbaId) {
    return (
      <ListPage breadcrumb={['Realizace', 'Harmonogram']} title="Harmonogram stavby" subtitle="Vyberte nebo založte stavbu">
        <StavbaGate message="Harmonogram je vázán na konkrétní stavbu." />
      </ListPage>
    );
  }

  if (listLoading) {
    return (
      <ListPage breadcrumb={['Realizace', 'Harmonogram']} title="Harmonogram stavby" subtitle="Načítám harmonogramy…">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>
      </ListPage>
    );
  }

  if (!firstId) {
    return (
      <ListPage
        breadcrumb={['Realizace', activeStavba?.nazev ?? '', 'Harmonogram'].filter(Boolean)}
        title="Harmonogram stavby"
        subtitle="Pro tuto stavbu ještě nebyl založen harmonogram."
        status="draft"
        statusText="Nezaložen"
      >
        <div style={{
          padding: 20,
          background: '#fff',
          border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(15, 20, 28, 0.05)',
        }}>
          <Button
            variant="primary"
            onClick={() => createHarmonogram.mutate({ nazev: 'Harmonogram stavby' })}
            disabled={createHarmonogram.isPending}
          >
            {createHarmonogram.isPending ? 'Zakládám…' : '+ Založit harmonogram'}
          </Button>
        </div>
      </ListPage>
    );
  }

  const tasks = h?.tasks ?? [];
  const avgProgress = tasks.length
    ? Math.round(tasks.reduce((a, t) => a + (t.progress || 0), 0) / tasks.length)
    : 0;

  return (
    <ListPage
      breadcrumb={['Realizace', activeStavba?.nazev ?? '', 'Harmonogram'].filter(Boolean)}
      title={h?.nazev ?? 'Harmonogram'}
      subtitle={
        detailLoading
          ? 'Načítám…'
          : `${tasks.length} činností · postup ${avgProgress} %`
      }
      status={avgProgress >= 100 ? 'done' : 'draft'}
      statusText={detailLoading ? 'Načítám…' : `Postup ${avgProgress} %`}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              if (activeStavbaId && firstId) {
                window.open(
                  `/api/stavby/${activeStavbaId}/harmonogramy/${firstId}/export`,
                  '_blank',
                );
              }
            }}
          >
            Export PDF
          </Button>
          <Button variant="primary" onClick={() => setFormOpen((v) => !v)}>
            + Činnost
          </Button>
        </>
      }
    >
      {formOpen && (
        <div style={{
          display: 'flex', gap: 12, padding: 12,
          background: '#fff',
          border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
          borderRadius: 4,
        }}>
          <input
            type="text"
            placeholder="Název činnosti"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: 'var(--sd-w-faf8ef, #faf8ef)',
              color: 'var(--sd-doc-ink)',
              border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
          <Button
            variant="primary"
            disabled={!newName.trim() || createTask.isPending}
            onClick={async () => {
              if (!newName.trim()) return;
              await createTask.mutateAsync({
                nazev: newName.trim(),
                poradi: tasks.length,
                progress: 0,
              });
              setNewName('');
              setFormOpen(false);
            }}
          >
            {createTask.isPending ? 'Ukládám…' : 'Přidat'}
          </Button>
        </div>
      )}

      <div className={styles.chart}>
        <div
          className={styles.chartHead}
          style={{ gridTemplateColumns: `240px repeat(${months.length}, 1fr)` }}
        >
          <div>Činnost</div>
          {months.map((m, idx) => (
            <div key={`${m.year}-${m.monthIndex}-${idx}`} style={{ textAlign: 'center' }}>
              {m.label}
            </div>
          ))}
        </div>

        {tasks.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
            Harmonogram je prázdný. Přidejte první činnost.
          </div>
        ) : (
          tasks.map((t) => {
            const od = parseDate(t.datum_od);
            const doD = parseDate(t.datum_do ?? t.datum_od);
            const startR = od ? ratio(od, months[0], months) : 0;
            const endR = doD ? ratio(doD, months[0], months) : startR + 0.05;
            const leftPct = startR * 100;
            const widthPct = Math.max(2, (endR - startR) * 100);
            const variant = progressVariant(t);
            return (
              <div
                key={t.id}
                className={styles.taskRow}
                style={{ gridTemplateColumns: '240px 1fr' }}
              >
                <div>
                  <div className={styles.taskName}>{t.nazev || '(bez názvu)'}</div>
                  <div className={styles.taskCat}>
                    {t.typ === 'milnik' ? 'Milník' : t.typ === 'faze' ? 'Fáze' : 'Úkol'}
                    {t.odpovedny && ` · ${t.odpovedny}`}
                  </div>
                </div>
                <div
                  className={styles.track}
                  style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}
                >
                  {months.map((m, idx) => (
                    <div key={`${m.year}-${m.monthIndex}-${idx}`} className={styles.trackCol} />
                  ))}
                  <div
                    className={cx(styles.bar, styles[variant])}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    title={`${t.nazev} · ${t.progress}%`}
                  >
                    {variant === 'done' ? '✓' : variant === 'running' ? `${t.progress}%` : ''}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </ListPage>
  );
}
